import { useEffect, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { useManagedOrg } from '@/hooks/useManagedOrg'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Avatar } from '@/components/Avatar'
import { LevelBadge } from '@/components/gamification/LevelBadge'
import type { Organization } from '@/domain/organization'
import type { LeaderboardEntry } from '@/domain/analytics'

// P2 organization-scoped leaderboard (initial version — advanced
// gamification/streaks remain P4). Individual Learners have no
// organization, so there is no cohort to rank them against; the page
// explains that rather than silently showing nothing. Orgs can turn the
// leaderboard off entirely via settings.leaderboardVisibility (already
// seeded false for Demo Enterprise) — respected here, not just cosmetic.
export function LeaderboardPage() {
  const { currentUser } = useAuth()
  const { organizations, orgAnalytics } = useServices()
  const { orgId, orgs, isSuperAdmin, selectedOrgId, setSelectedOrgId, loadingOrgs } = useManagedOrg()

  const [org, setOrg] = useState<Organization | null>(null)
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    Promise.all([organizations.getById(orgId), orgAnalytics.getLeaderboard(orgId, 20)]).then(([o, board]) => {
      setOrg(o)
      setEntries(board)
      setLoading(false)
    })
  }, [orgId, organizations, orgAnalytics])

  if (!currentUser) return null

  if (currentUser.orgId === null && currentUser.role !== 'super_admin') {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Leaderboards are for organizations</h1>
        <p className="mt-3 text-white/60">
          As an Individual Learner you're learning at your own pace outside any organization, so there's no
          organization cohort to rank you against. Your Cyber Points and badges are still tracked on your Profile.
        </p>
      </div>
    )
  }

  if (isSuperAdmin && loadingOrgs) return <LoadingSpinner label="Loading organizations…" />
  if (!orgId) return null
  if (loading) return <LoadingSpinner label="Loading leaderboard…" />

  const visibilityDisabled = org?.settings.leaderboardVisibility === false

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <span className="cyber-pill bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">Leaderboard</span>
          <h1 className="mt-3 font-display text-3xl font-bold text-white">Top learners this week</h1>
          <p className="mt-2 text-sm text-white/60">{org?.name}</p>
        </div>
        {isSuperAdmin && (
          <select
            value={selectedOrgId ?? ''}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="cyber-panel rounded-lg border border-white/10 bg-navy-900 px-3 py-2 text-sm text-white"
          >
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {visibilityDisabled ? (
        <div className="mt-8 cyber-panel p-8 text-center">
          <p className="text-sm text-white/60">
            {org?.name} has turned the leaderboard off for its learners (org privacy setting). Individual progress
            is still visible on each learner's own Profile.
          </p>
        </div>
      ) : entries.length === 0 ? (
        <div className="mt-8 cyber-panel p-8 text-center">
          <p className="text-sm text-white/60">
            No Cyber Points have been earned yet in {org?.name}. Pass an episode quiz to appear on the leaderboard.
          </p>
        </div>
      ) : (
        <ol className="mt-8 space-y-2">
          {entries.map((e) => (
            <li
              key={e.userId}
              className={`cyber-panel flex items-center gap-4 p-4 ${
                e.userId === currentUser.id ? 'ring-1 ring-teal-400/50' : ''
              }`}
            >
              <span className="w-8 text-center font-display text-lg font-bold text-white/40">{e.rank}</span>
              <Avatar name={e.displayName} color={e.avatarColor} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-white">
                  {e.displayName}
                  {e.userId === currentUser.id && <span className="ml-2 text-xs text-teal-300">(you)</span>}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <LevelBadge xpTotal={e.xpTotal} compact />
                  <p className="text-xs text-white/50">{e.badgesEarned} achievement{e.badgesEarned === 1 ? '' : 's'}</p>
                </div>
              </div>
              <span className="font-display text-lg font-bold gradient-text">{e.xpTotal} XP</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
